import {Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild} from '@angular/core';
import {Document} from '../../../../../../models/document';
import {AdditionalDocument} from '../../../../../../models/additional-document';
import {InfoDialogService} from '../../../../../../components/info-dialog/info-dialog.service';
import {PassportInfoComponent} from './infos/passport-info/passport-info.component';
import {IdInfoComponent} from './infos/id-info/id-info.component';
import {DrivingLicenseInfoComponent} from './infos/driving-license-info/driving-license-info.component';
import {AdditionalDocumentType} from '../../../../../../models/additional-document-type';
import {AllDocumentsInfoComponent} from './infos/all-documents-info/all-documents-info.component';
import {ToastrService} from 'ngx-toastr';
import {FormBuilder, FormControl, FormGroup, Validators} from '@angular/forms';
import {forbiddenValues} from '../../../../../../services/validators';

@Component({
  standalone: false,
  selector: 'app-identity-document',
  templateUrl: './identity-document.component.html',
  styleUrls: ['./identity-document.component.scss']
})
export class IdentityDocumentComponent implements OnInit {
  additionalDocumentType = AdditionalDocumentType;
  isBusy = new Array<boolean>();
  @Input() upload: (document: AdditionalDocument) => Promise<AdditionalDocument>;
  @Input() update: (document: AdditionalDocument) => Promise<AdditionalDocument>;
  @Input() remove: (document: AdditionalDocument) => Promise<boolean>;
  @Output() selectedDocumentChange = new EventEmitter<Document>();
  @Output() frontDocumentChange: EventEmitter<AdditionalDocument> = new EventEmitter<AdditionalDocument>();
  @Input() backDocument: AdditionalDocument;
  @Output() backDocumentChange: EventEmitter<AdditionalDocument> = new EventEmitter<AdditionalDocument>();
  @Input() additionalDocument: AdditionalDocument;
  @Output() additionalDocumentChange: EventEmitter<AdditionalDocument> = new EventEmitter<AdditionalDocument>();
  public documentForm: FormGroup;
  @Input() submitted: boolean;
  @ViewChild('documentFormRef') documentFormElement: ElementRef;
  public dateDay: number;
  public dateMonth: number;
  public dateYear: number;
  public years: number[];
  private expireDate: string;
  private _acceptExt = [
    '.pdf', '.jpg', '.jpeg', '.png'
  ];

  constructor(
    private formBuilder: FormBuilder,
    public infoDialogService: InfoDialogService,
    public toastr: ToastrService
  ) {
    const currentYear = new Date().getFullYear();
    this.years = Array(100).fill(0).map((x, i) => currentYear + i);
  }

  private _frontDocument: AdditionalDocument;

  get frontDocument(): AdditionalDocument {
    return this._frontDocument;
  }

  @Input() set frontDocument(value: AdditionalDocument) {
    this._frontDocument = value;
    this.updateSelectedDocument();
    this.updateExpireDate();
  }

  private _selectedDocument: Document;

  get selectedDocument(): Document {
    return this._selectedDocument;
  }

  @Input() set selectedDocument(value: Document) {
    this._selectedDocument = value;
    this.selectedDocumentChange.emit(value);
  }

  private _documents!: Document[];

  get documents(): Document[] {
    return this._documents;
  }

  @Input() set documents(value: Document[]) {
    this._documents = value;
    this.updateSelectedDocument();
  }

  get acceptFormats(): string {
    return this._acceptExt.join(',');
  }

  ngOnInit() {
    this.buildForm();
  }

  onDateChanged() {
    if (!this.dateMonth || !this.dateDay || !this.dateYear) {
      this.documentForm.controls.mmDate.setErrors(this.dateMonth ? null : {required: true});
      this.documentForm.controls.ddDate.setErrors(this.dateDay ? null : {required: true});
      this.documentForm.controls.yyyyDate.setErrors(this.dateYear ? null : {required: true});
    } else if (this.isPastDate()) {
      this.documentForm.controls.mmDate.setErrors({pastDate: true});
      this.documentForm.controls.ddDate.setErrors({pastDate: true});
      this.documentForm.controls.yyyyDate.setErrors({pastDate: true});
      this.expireDate = null;
      if (this.frontDocument) { this.frontDocument.expireDate = null; }
      if (this.backDocument) { this.backDocument.expireDate = null; }
    } else {
      this.documentForm.controls.mmDate.setErrors(null);
      this.documentForm.controls.ddDate.setErrors(null);
      this.documentForm.controls.yyyyDate.setErrors(null);
      this.expireDate =
        (Number(this.dateDay) < 10 ? '0' : '') + (Number(this.dateDay)) + '/' +
        (Number(this.dateMonth) < 10 ? '0' : '') + Number(this.dateMonth) + '/' +
        this.dateYear;

      this.updateDocuments();
    }
  }

  public isPastDate(): boolean {
    if (!this.dateYear || !this.dateMonth || !this.dateDay) {
      return false;
    }
    const selected = new Date(this.dateYear, this.dateMonth - 1, this.dateDay);
    const today = new Date();
    selected.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);
    return selected.getTime() < today.getTime();
  }

  public isPastDateError(): boolean {
    const ctrl = this.documentForm.controls.yyyyDate;
    return !!ctrl.errors?.pastDate && this.submitted;
  }

  public isRequiredField(fieldName: string): boolean {
    return this.documentForm.controls[fieldName].invalid
      && this.submitted
      && this.documentForm.controls[fieldName].errors.required;
  }

  async onLoadFront(event: any) {
    if (event.target.files && event.target.files.length > 0) {
      const file = event.target.files[0];
      this._frontDocument = await this.load(file, AdditionalDocumentType.ProofIdentityFront);
      this.frontDocumentChange.emit(this._frontDocument);
    }
  }

  async onLoadBack(event: any) {
    if (event.target.files && event.target.files.length > 0) {
      const file = event.target.files[0];
      this.backDocument = await this.load(file, AdditionalDocumentType.ProofIdentityBack);
      this.backDocumentChange.emit(this.backDocument);
    }
  }

  openInfo(document: Document) {
    switch (document.id) {
      case 'Passport':
        this.infoDialogService.show(PassportInfoComponent);
        break;
      case 'Id':
        this.infoDialogService.show(IdInfoComponent);
        break;
      case 'DrivingLicense':
        this.infoDialogService.show(DrivingLicenseInfoComponent);
        break;
    }
  }

  openAllInfo() {
    this.infoDialogService.show(AllDocumentsInfoComponent);
  }

  async onRemoveFront() {
    this.isBusy[AdditionalDocumentType.ProofIdentityFront] = true;
    const isDeleted = await this.remove(this._frontDocument);
    if (isDeleted) {
      this._frontDocument = null;
      this.frontDocumentChange.emit(this._frontDocument);
    }

    this.isBusy[AdditionalDocumentType.ProofIdentityFront] = false;
  }

  async onRemoveBack() {
    this.isBusy[AdditionalDocumentType.ProofIdentityBack] = true;
    const isDeleted = await this.remove(this.backDocument);
    if (isDeleted) {
      this.backDocument = null;
      this.backDocumentChange.emit(this.backDocument);
    }

    this.isBusy[AdditionalDocumentType.ProofIdentityBack] = false;
  }

  public onSelectedDocumentChange() {
    this.updateDocuments();
  }

  private updateExpireDate() {
    if (this.frontDocument && !!this.frontDocument.expireDate) {
      this.expireDate = this.frontDocument.expireDate;

      const split = this.frontDocument.expireDate.split('/');
      const day = Number(split[0]);
      const month = Number(split[1]);
      const year = Number(split[2]);

      if (day > 0) {
        this.dateDay = day;
        this.dateMonth = month;
        this.dateYear = year;
      }
    }
  }

  private load(file: any, documentType: AdditionalDocumentType): Promise<AdditionalDocument> {
    return new Promise<AdditionalDocument>((resolve) => {
      this.isBusy[documentType] = true;
      const reader = new FileReader();

      reader.onload = async (e) => {
        const additionalFile = {
          fileName: file.name,
          fileType: file.type,
          fileLength: file.size,
          type: this.selectedDocument.value,
          docType: documentType,
          value: (reader.result as string).split(',')[1],
          expireDate: this.selectedDocument.requiredDate ? this.expireDate : null
        } as AdditionalDocument;

        const document = await this.upload(additionalFile);
        this.isBusy[documentType] = false;
        resolve(document);
      };

      reader.onerror = (e) => {
        this.isBusy[documentType] = false;
        console.log(e);
      };

      reader.readAsDataURL(file);
    });
  }

  private updateDocuments(): void {
    if (this.frontDocument) {
      this.isBusy[AdditionalDocumentType.ProofIdentityFront] = true;
      this.frontDocument.expireDate = this.expireDate;
      this.frontDocument.type = this.selectedDocument.value;
      this.update(this.frontDocument).then().finally(() => this.isBusy[AdditionalDocumentType.ProofIdentityFront] = false);
    }

    if (this.backDocument) {
      this.isBusy[AdditionalDocumentType.ProofIdentityBack] = true;
      this.backDocument.expireDate = this.expireDate;
      this.backDocument.type = this.selectedDocument.value;
      this.update(this.backDocument).then().finally(() => this.isBusy[AdditionalDocumentType.ProofIdentityBack] = false);
    }
  }

  private updateSelectedDocument() {
    if (!this._documents) {
      return;
    }

    if (this._frontDocument &&
      this._frontDocument.type &&
      this._documents.some(x => x.value === this._frontDocument.type)) {
      this.selectedDocument = this._documents.find(x => x.value === this._frontDocument.type);
    } else if (this._documents.indexOf(this.selectedDocument) < 0) {
      this.selectedDocument = this._documents[0];
    }
  }

  private buildForm() {
    this.documentForm = this.formBuilder.group({
      mmDate: new FormControl('', [Validators.required]),
      ddDate: new FormControl('', [Validators.required]),
      yyyyDate: new FormControl('', [Validators.required])
    });
  }
}
